import React from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

type FullscreenOverlayProps = {
  open: boolean
  onClose?: () => void
  children: React.ReactNode
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  backdropSx?: SxProps<Theme>
  contentSx?: SxProps<Theme>
}

const FullscreenOverlay = ({
  open,
  onClose,
  children,
  ariaLabelledBy,
  ariaDescribedBy,
  backdropSx,
  contentSx,
}: FullscreenOverlayProps) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null)

  const baseBackdropSx = React.useMemo<SxProps<Theme>>(
    () => ({
      position: 'fixed',
      inset: 0,
      bgcolor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      p: 2,
    }),
    [],
  )

  const mergedBackdropSx = React.useMemo<SxProps<Theme>>(() => {
    if (!backdropSx) return baseBackdropSx
    return [baseBackdropSx, backdropSx] as SxProps<Theme>
  }, [backdropSx, baseBackdropSx])

  const baseContentSx = React.useMemo<SxProps<Theme>>(
    () => ({
      backgroundColor: 'white',
      borderRadius: 2,
      width: '100%',
      maxWidth: 520,
      minWidth: 320,
      textAlign: 'center',
      p: 4,
    }),
    [],
  )

  const mergedContentSx = React.useMemo<SxProps<Theme>>(() => {
    if (!contentSx) return baseContentSx
    return [baseContentSx, contentSx] as SxProps<Theme>
  }, [contentSx, baseContentSx])

  React.useEffect(() => {
    if (!open) return undefined
    if (typeof document === 'undefined') return undefined

    const previouslyFocused = document.activeElement as HTMLElement | null

    const focusInitialElement = () => {
      const focusable = contentRef.current
        ? (Array.from(contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)) as Array<HTMLElement>)
        : []
      const target = focusable[0] ?? contentRef.current
      target?.focus({ preventScroll: true })
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab') return

      const elements = contentRef.current
        ? (Array.from(contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)) as Array<HTMLElement>)
        : []

      if (elements.length === 0) {
        event.preventDefault()
        contentRef.current?.focus()
        return
      }

      const first = elements[0]
      const last = elements[elements.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (event.shiftKey) {
        if (active === first || active === contentRef.current) {
          event.preventDefault()
          last.focus()
        }
        return
      }

      if (active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    focusInitialElement()
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previouslyFocused?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <Box sx={mergedBackdropSx}>
      <Box
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        sx={mergedContentSx}

      >
        {children}
      </Box>
    </Box>
  )
}

export default FullscreenOverlay
